/**
 * 69.2b, the build path (decision 145 (3), "relays first, paid in time and a count"; DIRECTION §0.8, 69.4 step 1: "one
 * build path"): the beacon's plant (125a, core/beacon.ts) generalised to any kind. A `build` task through apply names the
 * kind and a spot; it is validated there (the kind's switch on, a count carried by someone named, ground that can be
 * built on). The planter, within the kind's reach of the spot, plants: a timed state (`plantLeft`, with `planting` naming
 * the kind — absent for the beacon) that holds them still with no firing; a shot or a jump stops it with the count kept.
 * Planted, the structure stands where the planter is, on the R12 body table, owned by the planter; the count is spent and
 * the task done. What pays: the beacon from its carrier's own (`beacons`), the relay from its squad's (`relays`).
 *
 * The beacon's own order (`{ kind: 'beacon' }`) is its kind on this path, and its plant runs as before, bit for bit (the
 * same checks in the same order, the same events). Pure; the Match steps it (`stepBuilds`, before the units act).
 */
import { yawFacing } from './hits';
import { headingOf } from './surfaceWalk';
import type { GameState, PlayerState, StructureState, UnitIntent, MatchSquad, Vec3, SquadOrder, BuildKind, GameConfig } from '../types';
import { BEACON, RELAY, FOB, HMG_BUILD, PHYS_DT } from '../constants';
import { fobsOn, fobsLeftFor, fobSupply, fobLinkRefusal } from './fob';
import { costsOn, costRefusal, storeFor, spend, buildCost, paidAtPlanting } from './buildCosts';
import { PERSON_BODY_HEIGHT, personSetOf } from './scaleProfile';
import { arcDistance } from './gameState';
import { structureHealth, initialSeats } from './body';
import { squadOf, squadTaskFor } from './squads';
import { getSurfaceRadius } from './surface';
import { structureFootprint } from './buildingScale';
import { sectorAt, liveSectors, sectorHolder as wholeSectorHolder } from './capturePoints';
import { dsin, dcos } from './dmath';
import { groundRefusal } from './ground';
import { seatBuild } from './buildSeat';
import { vecNormalize, vecAdd, vecScale, surfaceTangentFrame } from '../math';

/** A kind's dials: seconds held still to plant, and the reach (people) within which the planter plants at the spot. */
const KIND: Record<BuildKind, { seconds: number; reach: number }> = {
  beacon: { seconds: BEACON.plantSeconds, reach: BEACON.reach },
  relay: { seconds: RELAY.plantSeconds, reach: RELAY.reach },
  fob: { seconds: FOB.plantSeconds, reach: FOB.reach },   // 69.4
  hmg: { seconds: HMG_BUILD.plantSeconds, reach: HMG_BUILD.reach },   // 69.4 step 6e
};

/** Is this kind's switch on in the match? */
/** 65.22 (2): a built HMG's ring faces the way its builder does (its firing gap ahead of them, where they stand). */
function hmgYaw(p: Pick<PlayerState, 'surfaceDir' | 'heading'>): { yaw?: number } {
  const up = vecNormalize(p.surfaceDir), yaw = yawFacing(up, headingOf(p as PlayerState));
  return yaw ? { yaw } : {};
}

export function buildOn(config: Pick<GameConfig, 'beacons' | 'relays' | 'fobs' | 'buildKits'>, kind: BuildKind): boolean {
  if (kind === 'hmg') return !!config.buildKits;   // 69.4 step 6e: the HMG's kit is its count
  return kind === 'beacon' ? !!config.beacons : kind === 'fob' ? fobsOn(config) : !!config.relays;
}

/** The kind an order builds — the beacon's own order, or a `build` — else null. */
export function buildOrderKind(order: SquadOrder): BuildKind | null {
  return order.kind === 'beacon' ? 'beacon' : order.kind === 'build' ? order.structure : null;
}

/** The kind a planting under way puts up (absent: the beacon, 125a's planting). */
export function plantingKind(p: Pick<PlayerState, 'planting'>): BuildKind {
  return p.planting ?? 'beacon';
}

/** The reach within which a planter plants at the ordered spot, in world units at this scale. */
export function buildReach(state: Pick<GameState, 'config'>, kind: BuildKind): number {
  return KIND[kind].reach * PERSON_BODY_HEIGHT * personSetOf(state).k;
}

/** Seconds held still to plant this kind. */
export function buildSeconds(kind: BuildKind): number {
  return KIND[kind].seconds;
}

/** How many of this kind `p` can plant: the beacon from its own, the relay from its squad's, the FOB from its side's (69.4). */
export function buildsLeft(state: Pick<GameState, 'squads'> & Partial<Pick<GameState, 'fobsLeft' | 'buildKits'>>, p: Pick<PlayerState, 'id' | 'beacons' | 'teamIndex'>, kind: BuildKind): number {
  if (kind === 'hmg') return state.buildKits?.[p.teamIndex]?.hmg ?? 0;   // 69.4 step 6e: the side's HMG kits
  return kind === 'beacon' ? p.beacons ?? 0 : kind === 'fob' ? fobsLeftFor(state, p.teamIndex) : squadOf(state, p.id)?.relays ?? 0;
}

/** A kind the side's arrivals land at — the beacon (125a), and the relay where it carries drops (69.3a, GameConfig.drops). */
export function isSpawnKind(kind: BuildKind, config: Pick<GameConfig, 'drops'>): boolean {
  return kind === 'beacon' || (kind === 'relay' && !!config.drops);
}

/** A kind that stands only behind the front under Operations (69.6f's rule): the spawn kinds, and the FOB (69.4: a supply point the front has not reached). */
export function frontSiteKind(kind: BuildKind, config: Pick<GameConfig, 'drops'>): boolean {
  return isSpawnKind(kind, config) || kind === 'fob';
}

/** 69.6f: is sector `k` in play — the push's active sector, or (69.6e) one of the tug of war's live sectors? */
export function sectorInPlay(state: Pick<GameState, 'modeState'>, k: number): boolean {
  const pts = state.modeState.points, push = pts?.front ? pts.push : undefined;
  if (!push) return false;
  return push.shape === 'tug' ? liveSectors(pts!).includes(k) : k === push.active;
}

/**
 * 69.6f (decision 149): under Operations, the side that holds sector `k`. A push: the attackers the sectors they have taken
 * (and their base, before the first: −1), the defenders those beyond the active one (and their base, the last). The tug of
 * war (69.6e): the side holding every objective in it (lane D's `sectorHolder`), the attackers' base before the first. A
 * sector in play is the fight's: nobody's. Null too with no push.
 */
export function sectorHolder(state: Pick<GameState, 'modeState'>, k: number): number | null {
  const pts = state.modeState.points, push = pts?.front ? pts.push : undefined;
  if (!push || sectorInPlay(state, k)) return null;
  if (push.shape === 'tug') return k < 0 ? push.attacker : wholeSectorHolder(pts!.sites, k);
  return k < push.active ? push.attacker : push.defender;
}

/**
 * 69.6f (decision 149, the owner: "you can only put a relay down 1 segment behind the segment in play (and has to be owned
 * segment)"): may `team` stand a spawn structure at `at`? Under an Operations push only in a sector its side holds — at
 * least one behind the active sector, never in it (lane D's `sectorAt`). Null when it may (no push: anywhere), else why not.
 */
export function spawnSiteRefusal(state: Pick<GameState, 'modeState'>, team: number, at: { planetIndex: number; surfaceDir: Vec3 }): string | null {
  const front = state.modeState.points?.front;
  if (!front || !state.modeState.points?.push || at.planetIndex !== front.planetIndex) return null;
  const k = sectorAt(front, at.surfaceDir);
  if (sectorInPlay(state, k)) return 'Not in the sector in play: a spawn stands at least a sector behind the front';
  if (sectorHolder(state, k) !== team) return 'Only in a sector the side holds';
  return null;
}

/**
 * Can `team` build this kind here? Null when it can, else why not. A spawn kind under Operations: only a sector behind the
 * front that the side holds (`spawnSiteRefusal`, 69.6f). The beacon: otherwise anywhere (125a, unchanged). The relay:
 * ground no steeper than RELAY.grade within its footprint and a person (the coarse ground, eight ways), and clear of every
 * standing structure's footprint by RELAY.clear people.
 */
export function buildRefusal(state: Pick<GameState, 'planets' | 'structures' | 'config' | 'modeState'>, kind: BuildKind, at: { planetIndex: number; surfaceDir: Vec3 }, team: number): string | null {
  const site = frontSiteKind(kind, state.config) ? spawnSiteRefusal(state, team, at) : null;
  if (site) return site;
  // 69.4 step 6c: the ground — owned, contested or enemy — by the kind's row (the callers pass the whole state)
  const ground = groundRefusal(state as GameState, kind, at, team);
  if (ground) return ground;
  // 69.4 step 6b: a store of the side within reach that can pay (the callers pass the whole state)
  const cost = costRefusal(state as GameState, team, at, kind);
  if (cost) return cost;
  if (kind === 'beacon') return null;
  const planet = state.planets[at.planetIndex];
  if (!planet) return `No planet ${at.planetIndex} to build on`;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const half = structureFootprint({ type: kind }, state.config, planet.radius);
  const d = vecNormalize(at.surfaceDir), reach = half + person, h0 = getSurfaceRadius(planet, d);
  const { tx, ty } = surfaceTangentFrame(d);
  for (let k = 0; k < 8; k++) {
    const a = (k / 8) * Math.PI * 2, way = vecAdd(vecScale(tx, dcos(a)), vecScale(ty, dsin(a)));
    const rise = Math.abs(getSurfaceRadius(planet, vecNormalize(vecAdd(vecScale(d, planet.radius), vecScale(way, reach)))) - h0) / reach;
    if (rise > RELAY.grade) return 'The ground there is too steep to build on';
  }
  for (const s of state.structures) {
    if (s.health <= 0 || s.planetIndex !== at.planetIndex) continue;
    if (arcDistance(s.surfaceDir, d, planet.radius) < structureFootprint(s, state.config, planet.radius) + half + RELAY.clear * person) return 'Something already stands there';
  }
  // 69.4: a FOB only where its side's web would link it (the callers pass the whole state: the web needs the players)
  if (kind === 'fob') return fobLinkRefusal(state as GameState, team, at);
  return null;
}

/** Can `p` start planting `kind` at `spot` now: one left to plant, alive, on the ground and out of any seat, hands free, within reach. */
export function canBuild(state: Pick<GameState, 'planets' | 'config' | 'squads'> & Partial<Pick<GameState, 'fobsLeft' | 'buildKits'>>, p: PlayerState, kind: BuildKind, spot: { planetIndex: number; surfaceDir: Vec3 }): boolean {
  if (buildsLeft(state, p, kind) <= 0 || !p.alive || p.airborne || p.mount || p.planetIndex !== spot.planetIndex) return false;
  if (p.plantLeft !== undefined || p.reloadLeft !== undefined || p.switchLeft !== undefined || p.healLeft !== undefined || p.treating !== undefined) return false;
  return arcDistance(p.surfaceDir, spot.surfaceDir, state.planets[p.planetIndex].radius) <= buildReach(state, kind);
}

export type BuildEvent =
  | { kind: 'beaconPlanted'; unitId: number; structureId: number } | { kind: 'beaconStopped'; unitId: number }
  /** 69.2b: any other kind planted, or its planting stopped (the beacon keeps its own two events). */
  | { kind: 'built'; unitId: number; structureId: number; structure: BuildKind } | { kind: 'buildStopped'; unitId: number; structure: BuildKind };

/**
 * One tick of the build path (before the units act): every planting under way runs (planted when it runs out: the
 * structure stands where the planter is, the count spent, the task done) or stops (a shot or a jump; the count kept, the
 * task left for another try); then every unit with a build task in reach of its spot starts. `intents` is indexed like
 * `players`. Returns null when nothing changed.
 */
export function stepBuilds(state: Pick<GameState, 'players' | 'structures' | 'squads' | 'planets' | 'config' | 'modeState'> & Partial<Pick<GameState, 'fobsLeft' | 'convoys' | 'buildKits'>>, intents: readonly UnitIntent[], tick: number, dt: number): { players: PlayerState[]; structures: StructureState[]; squads: MatchSquad[] | undefined; fobsLeft?: Record<number, number>; convoys?: GameState['convoys']; buildKits?: GameState['buildKits']; events: BuildEvent[] } | null {
  let players: PlayerState[] | null = null, structures: StructureState[] | null = null, squads: MatchSquad[] | undefined | null = null;
  const costs = costsOn(state.config);   // 69.4 step 6b
  let convoys: GameState['convoys'] | null = null;
  let buildKits: GameState['buildKits'] | null = null;   // 69.4 step 6e: the HMG's kit, spent as one stands
  const hmgs = buildOn(state.config, 'hmg');
  const full = () => ({ ...state, structures: structures ?? state.structures, ...(convoys ? { convoys } : {}) }) as GameState;
  let fobsLeft: Record<number, number> | null = null;   // 69.4: the side's count, spent as one stands
  const events: BuildEvent[] = [];
  const beacons = !!state.config.beacons, relays = !!state.config.relays, fobs = fobsOn(state.config);
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i], intent = intents[i];
    if (!p.alive) continue;
    if (p.plantLeft !== undefined) {
      const kind = plantingKind(p);
      if (intent.fire || intent.jump || p.airborne || p.mount) {
        (players ??= state.players.slice())[i] = { ...p, plantLeft: undefined, ...(p.planting ? { planting: undefined } : {}) };
        events.push(kind === 'beacon' ? { kind: 'beaconStopped', unitId: p.id } : { kind: 'buildStopped', unitId: p.id, structure: kind });
        continue;
      }
      if (p.plantLeft - dt > 0) { (players ??= state.players.slice())[i] = { ...p, plantLeft: p.plantLeft - dt }; continue; }
      // 69.4 step 6b: paid as it stands, from the nearest store in reach — none left: the planting stops, the count kept
      if (costs && paidAtPlanting(kind)) {
        const store = storeFor(full(), p.teamIndex, p, buildCost(kind), kind);
        if (!store) {
          (players ??= state.players.slice())[i] = { ...p, plantLeft: undefined, ...(p.planting ? { planting: undefined } : {}) };
          events.push(kind === 'beacon' ? { kind: 'beaconStopped', unitId: p.id } : { kind: 'buildStopped', unitId: p.id, structure: kind });
          continue;
        }
        const paid = spend(full(), store, buildCost(kind));
        structures = paid.structures === state.structures ? structures : paid.structures;
        if (paid.convoys !== (convoys ?? state.convoys)) convoys = paid.convoys ?? null;
      }
      // planted: the structure where the planter stands, its health the body table's
      const all = structures ?? state.structures;
      const id = all.reduce((m, s) => Math.max(m, s.id), -1) + 1;
      (structures ??= state.structures.slice()).push(seatBuild(state as GameState, { id, type: kind, planetIndex: p.planetIndex, surfaceDir: vecNormalize(p.surfaceDir), health: structureHealth(kind), ownerId: p.id,
        ...(kind === 'relay' && state.config.drops ? { drops: RELAY.drops } : {}),   // 69.3a: a relay carries its drops
        ...(kind === 'fob' ? { supply: fobSupply(p.teamIndex, state.config) } : {}),   // 69.4: a FOB's own crate, full (69.30: its rounds)
        ...(kind === 'hmg' ? { seats: initialSeats('hmg'), ...hmgYaw(p) } : {}) }, tick * PHYS_DT));   // (65.22 (2): its gap the way its builder faces)   // 69.4 step 6e: the emplaced HMG's body, its seat empty; 67.52: on its spot's fine ground
      const cur: Pick<GameState, 'squads'> = { squads: squads ?? state.squads };
      if (kind === 'beacon') (players ??= state.players.slice())[i] = { ...p, plantLeft: undefined, beacons: (p.beacons ?? 0) - 1 };
      else if (kind === 'hmg') {
        // 69.4 step 6e: the side's HMG kit spent
        (players ??= state.players.slice())[i] = { ...p, plantLeft: undefined, planting: undefined };
        const kits: NonNullable<GameState['buildKits']> = buildKits ?? state.buildKits ?? {}, kit = kits[p.teamIndex];
        if (kit) buildKits = { ...kits, [p.teamIndex]: { ...kit, hmg: Math.max(0, kit.hmg - 1) } };
      } else if (kind === 'fob') {
        // 69.4: the side's count spent
        (players ??= state.players.slice())[i] = { ...p, plantLeft: undefined, planting: undefined };
        const left: Record<number, number> = fobsLeft ?? state.fobsLeft ?? {};
        fobsLeft = { ...left, [p.teamIndex]: Math.max(0, (left[p.teamIndex] ?? 0) - 1) };
      } else {
        (players ??= state.players.slice())[i] = { ...p, plantLeft: undefined, planting: undefined };
        // the squad's count spent
        const sq = squadOf(cur, p.id);
        if (sq) squads = (cur.squads ?? []).map(s => (s === sq ? { ...s, relays: Math.max(0, (s.relays ?? 0) - 1) } : s));
      }
      // the task is done: off its squad
      const task = squadTaskFor({ squads: squads ?? state.squads }, p.id, tick);
      if (task && buildOrderKind(task.order) === kind) squads = (squads ?? state.squads ?? []).map(sq => (sq.tasks.includes(task) ? { ...sq, tasks: sq.tasks.filter(t => t !== task) } : sq));
      events.push(kind === 'beacon' ? { kind: 'beaconPlanted', unitId: p.id, structureId: id } : { kind: 'built', unitId: p.id, structureId: id, structure: kind });
      continue;
    }
    // only a unit that could build something asks for its task (the beacon's carriers, a relay squad's members)
    if (!(beacons && (p.beacons ?? 0) > 0) && !(relays && buildsLeft(squads ? { squads } : state, p, 'relay') > 0) && !(fobs && fobsLeftFor(fobsLeft ? { fobsLeft } : state, p.teamIndex) > 0)
      && !(hmgs && buildsLeft({ squads: state.squads, buildKits: buildKits ?? state.buildKits }, p, 'hmg') > 0)) continue;
    const task = squadTaskFor(state, p.id, tick);
    const kind = task ? buildOrderKind(task.order) : null;
    if (!task || !kind || !buildOn(state.config, kind) || intent.fire || intent.jump) continue;
    if (canBuild({ ...state, ...(squads ? { squads } : {}), ...(fobsLeft ? { fobsLeft } : {}), ...(buildKits ? { buildKits } : {}) }, p, kind, task.order as { planetIndex: number; surfaceDir: Vec3 })) {
      // 69.6f: the front may have moved since the order — a spawn no longer allowed where the planter stands is given up (the task done)
      if (frontSiteKind(kind, state.config) && spawnSiteRefusal(state, p.teamIndex, p)) {
        squads = (squads ?? state.squads ?? []).map(sq => (sq.tasks.includes(task) ? { ...sq, tasks: sq.tasks.filter(t => t !== task) } : sq));
        continue;
      }
      if (costs && paidAtPlanting(kind) && !storeFor(full(), p.teamIndex, p, buildCost(kind), kind)) continue;   // 69.4 step 6b: no store in reach can pay yet — it waits, the task kept
      (players ??= state.players.slice())[i] = { ...p, plantLeft: buildSeconds(kind), ...(kind === 'beacon' ? {} : { planting: kind }) };
    }
  }
  if (!players && !structures && squads === null && fobsLeft === null && convoys === null && buildKits === null) return null;
  return { players: players ?? state.players.slice(), structures: structures ?? state.structures, squads: squads === null ? state.squads : squads, ...(fobsLeft ? { fobsLeft } : {}), ...(convoys ? { convoys } : {}), ...(buildKits ? { buildKits } : {}), events };
}
